// hooks/useExcelPaste.js
import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

export const useExcelPaste = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [rawData, setRawData] = useState([]); // Untuk menyimpan data mentah termasuk header

  const parseExcelClipboardData = useCallback((pastedText) => {
    try {
      // Membersihkan dan memproses data yang di-paste
      const rows = pastedText
        .split(/\r?\n/) // Handle both \n and \r\n
        .map(row => row.split('\t').map(cell => cell.trim()))
        .filter(row => row.length > 0 && row.some(cell => cell !== ''));

      if (rows.length === 0) {
        return { success: false, message: 'Tidak ada data yang ditemukan' };
      }

      const headers = rows[0];
      const data = rows.slice(1).map(row => {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      // Simpan data mentah untuk preview
      setRawData(rows);
      setHeaders(headers);
      setData(data);

      return { 
        success: true, 
        headers, 
        data,
        rawData: rows,
        summary: {
          rows: data.length,
          columns: headers.length
        }
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }, []);

  const parseExcelFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert ke array of arrays
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0];
            const data = jsonData.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, idx) => {
                obj[header] = row[idx] || '';
              });
              return obj;
            });

            setRawData(jsonData);
            setHeaders(headers);
            setData(data);

            resolve({ 
              success: true, 
              headers, 
              data,
              rawData: jsonData,
              summary: {
                rows: data.length,
                columns: headers.length
              }
            });
          } else {
            resolve({ success: false, message: 'File kosong' });
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Gagal membaca file'));
      reader.readAsBinaryString(file);
    });
  }, []);

  const exportToExcel = useCallback((fileName = 'data-export.xlsx') => {
    if (data.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(data, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
  }, [data, headers]);

  const clearData = useCallback(() => {
    setData([]);
    setHeaders([]);
    setRawData([]);
  }, []);

  return {
    data,
    headers,
    rawData,
    parseExcelClipboardData,
    parseExcelFile,
    exportToExcel,
    clearData,
    hasData: data.length > 0,
    summary: {
      rows: data.length,
      columns: headers.length
    }
  };
};